import { useMemo, useState } from 'react';
import type { DashboardWidgetId } from '@reform-lite/contracts';
import { useT } from '@/i18n/use-t';
import { useHasPermission } from '@/auth/auth-store';
import { Button } from '@/components/ui/button';
import { extractApiError } from '@/lib/api-error';
import { useDashboardWidgets, useUpdateDashboardWidgets } from '@/hooks/use-dashboard-widgets';

/**
 * Settings → Dashboard widgets (RL-377, concept Q4).
 *
 * A tenant admin chooses which tiles the tenant's dashboard shows and in what
 * order, behind `dashboard.write`. The operator portal keeps module
 * entitlements and the built-in default; a tenant rearranging its own tiles is
 * an ordinary presentation choice and never a request to us.
 *
 * Two things the concept is careful to separate, and so is this page:
 *
 * - **Availability** is the module entitlement the server sends as
 *   `available`. A tenant without `kitchen` is never offered the kitchen
 *   widget, and the client does not second-guess that list — a permission is
 *   not a licence, so there is nothing here to recompute it from.
 * - **Selection** is which of the available widgets are on, and in what
 *   order, because the order is the render order.
 *
 * A widget that is stored but no longer available — an entitlement withdrawn
 * after somebody chose it — is dropped on the way in rather than shown as a
 * ghost the admin cannot explain, so saving the page also cleans it out.
 *
 * Saving is explicit. Toggling nine checkboxes one at a time would be nine
 * writes and nine audit rows for one decision, and it leaves no way to change
 * your mind. No drag-and-drop: concept §7 rules it out, and move up / move
 * down works from a keyboard without any of it.
 */

/** Baked English, kept beside the catalogue keys the rows are declared under. */
const WIDGET_COPY: Record<DashboardWidgetId, { name: string; description: string }> = {
  sales: {
    name: 'Sales today',
    description: 'Gross sales, returns and net for today, with receipt counts.',
  },
  posOnline: {
    name: 'POS online',
    description: 'How many tills are reachable right now, out of the total.',
  },
  syncHealth: {
    name: 'Sync health',
    description: 'Terminals counted by their worst sync lag, including those that never reported.',
  },
  openShifts: {
    name: 'Open shifts',
    description: 'Shifts still trading, and how many were closed abnormally.',
  },
  cashVariance: {
    name: 'Cash variance today',
    description: 'Cash drawer variance summed across today’s shifts.',
  },
  approvals: {
    name: 'Pending approvals',
    description: 'Requests waiting for someone to action them.',
  },
  payments: {
    name: 'Stuck payments',
    description: 'Reconciliation items waiting for someone to action them.',
  },
  kitchenOrders: {
    name: 'Kitchen orders',
    description: 'Kitchen orders still in play, and the stores they are in.',
  },
  weightCoverage: {
    name: 'Weight coverage',
    description: 'Share of weighed items covered, as the coverage report counts it.',
  },
  fiscalHolds: {
    name: 'Held fiscal sales',
    description:
      'Sales self-checkout lanes could not fiscalise, and how many need a person to retry or void them.',
  },
};

function sameOrder(a: readonly DashboardWidgetId[], b: readonly DashboardWidgetId[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

function displayDate(value: string | null, emptyValue: string): string {
  if (value === null) return emptyValue;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}

export function DashboardWidgetsPage() {
  const t = useT('admin_ui');
  const canWrite = useHasPermission('dashboard.write');
  const selectionQuery = useDashboardWidgets(canWrite);
  const updateWidgets = useUpdateDashboardWidgets();
  const [draft, setDraft] = useState<readonly DashboardWidgetId[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selection = selectionQuery.data;

  /**
   * The server's list, minus anything the tenant is no longer entitled to.
   * `available` is the authority: a stored id outside it cannot be selected,
   * shown, or saved back.
   */
  const stored = useMemo<readonly DashboardWidgetId[]>(() => {
    if (selection === undefined) return [];
    const available = new Set(selection.available);
    return selection.widgets.filter((id) => available.has(id));
  }, [selection]);

  const selected = draft ?? stored;
  const offered = selection?.available ?? [];
  const unselected = offered.filter((id) => !selected.includes(id));
  const dirty = draft !== null && !sameOrder(draft, stored);

  function widgetName(id: DashboardWidgetId): string {
    return t(`dashboardSettings.widget.${id}.name`, WIDGET_COPY[id].name);
  }

  function widgetDescription(id: DashboardWidgetId): string {
    return t(`dashboardSettings.widget.${id}.description`, WIDGET_COPY[id].description);
  }

  function toggle(id: DashboardWidgetId, on: boolean): void {
    // Availability is not negotiable on the client: a widget the tenant is not
    // entitled to can never be added, however it got into the stored list.
    if (on && !offered.includes(id)) return;
    setDraft(on ? [...selected, id] : selected.filter((candidate) => candidate !== id));
  }

  function move(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    const moved = next[index]!;
    next[index] = next[target]!;
    next[target] = moved;
    setDraft(next);
  }

  async function save(): Promise<void> {
    setError(null);
    try {
      await updateWidgets.mutateAsync(selected);
      setDraft(null);
    } catch (cause) {
      setError(extractApiError(cause));
    }
  }

  /**
   * Clearing the stored choice, not writing the default out as a choice — an
   * empty list is the wire's way of saying "nobody has chosen", so the tenant
   * goes back to tracking the built-in default rather than freezing today's
   * copy of it.
   */
  async function resetToDefault(): Promise<void> {
    setError(null);
    try {
      await updateWidgets.mutateAsync([]);
      setDraft(null);
    } catch (cause) {
      setError(extractApiError(cause));
    }
  }

  if (!canWrite) {
    return (
      <div className="max-w-4xl space-y-2">
        <h1 className="text-lg font-semibold">
          {t('dashboardSettings.title', 'Dashboard widgets')}
        </h1>
        <p className="text-sm text-destructive">
          {t(
            'dashboardSettings.noAccess',
            'You do not have permission to configure the dashboard.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold">
          {t('dashboardSettings.title', 'Dashboard widgets')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'dashboardSettings.description',
            'Choose which tiles this tenant’s dashboard shows, and the order they appear in. Only the widgets your licence covers are offered.',
          )}
        </p>
      </header>

      {selectionQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : selectionQuery.isError || selection === undefined ? (
        <p className="text-sm text-destructive">
          {t('dashboardSettings.loadError', 'Could not load the dashboard widget selection.')}
        </p>
      ) : (
        <>
          <section className="space-y-2 rounded-lg border border-border bg-card p-4 text-sm">
            {selection.isDefault ? (
              <p className="text-muted-foreground">
                {t(
                  'dashboardSettings.onDefault',
                  'This tenant is on the built-in default set. Nobody has chosen yet.',
                )}
              </p>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-muted-foreground">
                  {t('dashboardSettings.lastChanged', 'Last changed {when} by {who}.')
                    .replace(
                      '{when}',
                      displayDate(selection.updatedAt, t('dashboardSettings.never', 'never')),
                    )
                    .replace(
                      '{who}',
                      selection.updatedBy ?? t('dashboardSettings.unknownUser', 'someone'),
                    )}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={updateWidgets.isPending}
                  onClick={resetToDefault}
                >
                  {t('dashboardSettings.resetToDefault', 'Reset to default')}
                </Button>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('dashboardSettings.shown', 'On the dashboard')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  'dashboardSettings.shownHelp',
                  'Tiles render in this order, left to right and top to bottom. At least one must stay on; use Reset to default to restore the built-in set.',
                )}
              </p>
            </div>
            {selected.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'dashboardSettings.noneSelected',
                  'No widgets are on. The dashboard will show nothing until you turn one on or reset to the default.',
                )}
              </p>
            ) : (
              <ol className="space-y-2">
                {selected.map((id, index) => (
                  <li
                    key={id}
                    className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-3"
                  >
                    <label className="flex flex-1 items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked
                        // The API reserves [] for "reset to defaults". Letting
                        // the editor preview an empty authored selection would
                        // promise a state that Save can never persist.
                        disabled={selected.length === 1}
                        onChange={(event) => toggle(id, event.target.checked)}
                      />
                      <span>
                        <span className="block text-sm font-medium">{widgetName(id)}</span>
                        <span className="block text-xs text-muted-foreground">
                          {widgetDescription(id)}
                        </span>
                      </span>
                    </label>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={index === 0}
                        aria-label={t('dashboardSettings.moveUp', 'Move {widget} up').replace(
                          '{widget}',
                          widgetName(id),
                        )}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={index === selected.length - 1}
                        aria-label={t('dashboardSettings.moveDown', 'Move {widget} down').replace(
                          '{widget}',
                          widgetName(id),
                        )}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </Button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('dashboardSettings.hidden', 'Not on the dashboard')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  'dashboardSettings.hiddenHelp',
                  'Turning one on adds it to the end of the list above.',
                )}
              </p>
            </div>
            {unselected.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'dashboardSettings.allSelected',
                  'Every widget this tenant is entitled to is already on the dashboard.',
                )}
              </p>
            ) : (
              <ul className="space-y-2">
                {unselected.map((id) => (
                  <li key={id} className="rounded-lg border border-border bg-card p-3">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={false}
                        onChange={(event) => toggle(id, event.target.checked)}
                      />
                      <span>
                        <span className="block text-sm font-medium">{widgetName(id)}</span>
                        <span className="block text-xs text-muted-foreground">
                          {widgetDescription(id)}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {error !== null && <p className="text-sm text-destructive">{error}</p>}

          <footer className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
            <Button type="button" disabled={!dirty || updateWidgets.isPending} onClick={save}>
              {updateWidgets.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!dirty || updateWidgets.isPending}
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
            >
              {t('common.discardChanges', 'Discard changes')}
            </Button>
            {dirty && (
              <span className="text-xs text-muted-foreground">
                {t('dashboardSettings.unsaved', 'Unsaved changes')}
              </span>
            )}
          </footer>
        </>
      )}
    </div>
  );
}
